import Phaser from 'phaser';
import { COLORS } from './constants/colors';
// PRE-WARM циклической зависимости scale ↔ platform ↔ Player. Сцены импортят
// scale.ts первым (MapScene → getUiScale), и цепочка раскручивается так:
// scale → platform → Player → scale(PARTIAL!) → TDZ на PLAYER_SCALE.
// Если же стартовать с platform.ts — порядок безопасный: platform → field →
// scale(полностью) → ✅ → platform → Player → scale(готов) → ✅.
// Раньше эту роль играл MenuScene (он импортил isMobile из platform), после
// его выноса из бутстрапа этот импорт стал явной "первой ступенью". НЕ удалять.
import './utils/platform';
import { BootScene } from './scenes/BootScene';
import { MapScene } from './scenes/MapScene';
import { GameScene } from './scenes/GameScene';
import { GameOverScene } from './scenes/GameOverScene';
import { PortraitOverlayScene } from './scenes/PortraitOverlayScene';
import { markPhaserCreated, markRotateStart } from './platform/perf';
import * as analytics from './analytics/analytics';

// Telegram Mini App: разворачиваем на максимум ДО создания Phaser.Game, чтобы
// стартовое окно было уже расширено (иначе игра подхватит compact-высоту, потом
// resize-событие перестроит — лишний цикл рендера). Вне TG (обычный браузер) tg
// undefined → no-op. requestFullscreen появился в Bot API 8.0 — обернуто в try/catch
// и проверку наличия метода для совместимости со старыми клиентами.
const tg = window.Telegram?.WebApp;
if (tg) {
  tg.ready?.();
  tg.expand?.();
  if (typeof tg.requestFullscreen === 'function') {
    try {
      tg.requestFullscreen();
    } catch {
      // requestFullscreen может требовать user gesture или быть недоступен — не критично.
    }
  }
}

// PostHog: init ПОСЛЕ Telegram-инициализации, чтобы identify мог использовать
// initDataUnsafe.user.id. В DEV / без ENV — no-op (см. analytics.init).
analytics.init();
const platform: string = tg?.initData ? 'telegram' : 'web';
analytics.markSessionStart({ platform });

// session_end: beforeunload ненадёжен на iOS/TG webview (страница «замирает»
// без unload), поэтому дублируем visibilitychange→hidden. Идемпотентно
// через флаг sentSessionEnd внутри analytics.
window.addEventListener('beforeunload', () => {
  analytics.endSession();
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') analytics.endSession();
});

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  backgroundColor: COLORS.darkGray,
  render: {
    pixelArt: false,
    antialias: true,
    roundPixels: true,
  },
  scale: {
    // RESIZE — сцена занимает весь viewport, размер = окно. Поле = весь canvas,
    // никаких min-max капов. Адаптивная вёрстка через fieldGrid 40×20 — клетки
    // пропорциональны размеру окна (см. constants/field.ts).
    mode: Phaser.Scale.RESIZE,
    parent: 'game',
    width: window.innerWidth,
    height: window.innerHeight,
  },
  input: {
    activePointers: 3,
  },
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 0 },
      debug: false,
    },
  },
  // PortraitOverlay — последняя в массиве, рендерится поверх остальных (active=true,
  // висит сама с момента старта игры, показывается только в портретной ориентации).
  scene: [BootScene, MapScene, GameScene, GameOverScene, PortraitOverlayScene],
};

const game = new Phaser.Game(config);
markPhaserCreated();

// Dev-perf-overlay отключён — плашка в правом верхнем углу перекрывала HUD игры.
// Модуль DevPerfOverlay.ts и perf.ts остаются на месте: коллектор perf.ts
// работает как раньше и позже переиспользуется под PostHog-события (W14),
// overlay можно снова смонтировать вручную если понадобится дебажить.

// Phaser RESIZE сам слушает window.resize, но на iOS Safari/мобильных WebKit
// resize ПОСЛЕ orientationchange приходит с задержкой 1-3 секунды (карта 3-5с
// "не прогружается"). Подписки в порядке от самой ранней до самой поздней:
//   1) matchMedia(orientation) — наиболее ранняя реакция, срабатывает в момент
//      смены ориентации ДО фактического resize окна.
//   2) screen.orientation API — современный путь, есть в Chrome 38+, Safari 16.4+.
//   3) orientationchange + один tail-refresh (200 мс) — fallback для старых WebKit.
const triggerRefresh = (): void => {
  // Первая точка rotate-цикла (idempotent в течение окна 3с, см. perf.ts).
  // visualViewport.resize внизу этот же handler НЕ вызывает — там URL-bar
  // animation, не пользовательский поворот.
  markRotateStart();
  game.scale.refresh();
};

const landscapeMQ = window.matchMedia('(orientation: landscape)');
if (typeof landscapeMQ.addEventListener === 'function') {
  landscapeMQ.addEventListener('change', triggerRefresh);
}

if (window.screen?.orientation?.addEventListener) {
  window.screen.orientation.addEventListener('change', triggerRefresh);
}

window.addEventListener('orientationchange', () => {
  triggerRefresh();
  setTimeout(triggerRefresh, 200);
});

// iOS Safari: показ/скрытие нижней URL-панели меняет visualViewport.height,
// но НЕ всегда эмитит window.resize. Без этого карта обрезалась снизу — нижние
// ряды точек уходили под панель браузера. С 100dvh контейнер #game сжимается
// корректно, тут отдельно дёрнем Phaser refresh чтобы canvas обновил размер.
//
// THROTTLE 150 мс: после orientationchange iOS Safari выдаёт 5-15 промежуточных
// visualViewport.resize за ~2 сек (анимация URL-bar). Каждый scale.refresh()
// триггерит resize-event в КАЖДОЙ сцене → MapScene пересчитывает 45 точек +
// cover-fit фона. На младших iPhone это 200-400 мс на проход → суммарно 3-5 с.
// throttle первой и трейлинг-refresh = максимум 2 пересчёта вместо 15.
if (window.visualViewport) {
  let lastRefreshAt = 0;
  let trailingTimer: number | null = null;
  const THROTTLE_MS = 150;
  window.visualViewport.addEventListener('resize', () => {
    const now = performance.now();
    const since = now - lastRefreshAt;
    if (since >= THROTTLE_MS) {
      lastRefreshAt = now;
      triggerRefresh();
      return;
    }
    if (trailingTimer !== null) return;
    trailingTimer = window.setTimeout(() => {
      trailingTimer = null;
      lastRefreshAt = performance.now();
      triggerRefresh();
    }, THROTTLE_MS - since);
  });
}
